/**
 * `<AttachmentLightbox>` — fullscreen viewer for a single card attachment.
 *
 * Portals to `document.body` (escapes any overflow-clip / modal), darkens
 * the surround, and closes on Esc, backdrop click, or the × button. The
 * Escape handler `preventDefault()`s so a parent `<Modal>`'s document-level
 * Escape listener sees `defaultPrevented` and does NOT also close (the
 * nested-escape convention — see the `[FE/Modal]` pitfall in CLAUDE.md).
 *
 * Two render modes:
 *   - **Image** — draws the image. Chosen when a `url` was supplied and
 *     nothing about the content type says it isn't an image, so the
 *     description's `image` NodeView (which knows it holds an image but
 *     carries no MIME on the node) lands here without a synthetic type.
 *   - **Everything else** — a MIME-derived glyph plus the filename and
 *     size. Deliberately no inline preview for PDFs / text / video, per
 *     product direction: the lightbox identifies the file, and the actions
 *     get you to the content. `Open in new tab` is offered alongside
 *     Download precisely so a PDF stays readable without one.
 *
 * Generic on purpose: no carousel (unlike the chat `MessageImageLightbox`).
 * `attachmentId` is the switch for the download affordance — downloading is
 * routed through the id rather than the URL on screen because the BE has to
 * re-sign the object with `Content-Disposition: attachment`; see
 * `downloadAttachment`. An image with no attachment behind it (a legacy
 * third-party URL from the old toolbar embed) simply gets no button.
 */

import { Download, ExternalLink, X } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { useToast } from '@/components/ToastProvider'
import { formatBytes } from '@/features/chat/lib/format-bytes'
import { isImageContentType, resolveFileIcon } from '@/features/uploads/file-icon'
import { downloadAttachment } from '@/lib/download-file'

interface AttachmentLightboxProps {
  /** Display name, and the label under the glyph in non-image mode. */
  filename: string
  /** MIME type. Omit when the caller already knows it holds an image. */
  contentType?: string | null
  sizeBytes?: number | null
  /** Resolved URL, for image rendering only. */
  url?: string | null
  /**
   * Attachment backing this file. Omit to hide the download button — the
   * right call for anything we can't re-sign server-side.
   */
  attachmentId?: string | null
  onClose: () => void
}

export function AttachmentLightbox({
  filename,
  contentType,
  sizeBytes,
  url,
  attachmentId,
  onClose,
}: AttachmentLightboxProps) {
  const { t, i18n } = useTranslation('uploads')
  const toast = useToast()
  const [downloading, setDownloading] = useState(false)

  // An explicit non-image content type wins; absent one, a supplied URL is
  // taken as an image (the description image NodeView's case).
  const showsImage = Boolean(url) && (contentType == null || isImageContentType(contentType))
  const Icon = resolveFileIcon(contentType, filename)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        onClose()
      }
    }
    // CAPTURE phase, and that is load-bearing. The parent `<Modal>` also
    // installs a document-level Escape listener, and it gates on
    // `event.defaultPrevented` so an inner surface can claim the key. That
    // hand-off only works if the inner listener runs FIRST — but both are
    // on `document`, where same-phase listeners fire in REGISTRATION
    // order, and the Modal is open (hence registered) before this lightbox
    // ever mounts. On the bubble phase the Modal therefore won a race it
    // was written to lose, and Escape closed the whole card modal out from
    // under the lightbox. Capture runs ahead of every bubble listener
    // regardless of registration order, so `preventDefault` +
    // `stopPropagation` land before the Modal is consulted.
    document.addEventListener('keydown', onKey, true)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey, true)
      document.body.style.overflow = prevOverflow
    }
  }, [onClose])

  const handleDownload = useCallback(async () => {
    if (!attachmentId || downloading) return
    setDownloading(true)
    try {
      await downloadAttachment(attachmentId)
    } catch {
      toast.error(t('lightbox.downloadFailed'))
    } finally {
      setDownloading(false)
    }
  }, [attachmentId, downloading, t, toast])

  return createPortal(
    <div
      className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/85 p-6"
      // biome-ignore lint/a11y/useSemanticElements: native <dialog> has divergent browser behavior; ARIA dialog pattern is intentional here, matching Modal.tsx.
      role="dialog"
      aria-modal="true"
      aria-label={filename}
      // Close only on a click that landed on the backdrop ITSELF. Gating on
      // the target beats `stopPropagation` on every child: children stay
      // plain, non-interactive markup instead of each needing a click
      // handler (and an a11y suppression) purely to defend against the
      // parent.
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="absolute end-4 top-4 flex items-center gap-2">
        {attachmentId ? (
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading}
            aria-label={t('lightbox.download')}
            title={t('lightbox.download')}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:opacity-60"
          >
            <Download size={18} aria-hidden="true" />
          </button>
        ) : null}
        <button
          type="button"
          onClick={onClose}
          aria-label={t('lightbox.close')}
          title={t('lightbox.close')}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
        >
          <X size={20} aria-hidden="true" />
        </button>
      </div>

      {showsImage && url ? (
        <img
          src={url}
          alt={filename}
          // No `crossOrigin` — the image is only ever displayed here.
          // Download rides a separately-signed URL, so the CORS-read
          // handshake the chat / wiki lightboxes need doesn't apply, and
          // requesting it would break rendering for any origin that omits
          // the header.
          className="max-h-full max-w-full rounded object-contain"
        />
      ) : (
        <div className="flex max-w-full flex-col items-center gap-4 rounded-lg bg-white/5 px-10 py-12 text-center">
          <Icon size={72} aria-hidden="true" className="text-white/70" strokeWidth={1.25} />
          <div className="flex max-w-md flex-col gap-1">
            <p className="truncate text-base font-medium text-white" title={filename}>
              {filename}
            </p>
            {sizeBytes != null && (
              <p className="text-sm text-white/60">{formatBytes(sizeBytes, i18n.language)}</p>
            )}
          </div>
          {url ? (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-md bg-white/10 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              <ExternalLink size={16} aria-hidden="true" />
              {t('lightbox.openInNewTab')}
            </a>
          ) : null}
        </div>
      )}
    </div>,
    document.body,
  )
}
